// import InputAdornment from '@mui/material/InputAdornment';
import { Typography, Link } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import get from 'lodash/get';
import moment from 'moment-timezone';
import thousands from 'thousands';
import common from './common';

import StatusChip from 'components/status-chip';
import { localizeFund, TranslatedText } from 'lib/utils';

// Status constants matching the API model
export const STATUS_CANCELLED = 0;
export const STATUS_PENDING = 1;
export const STATUS_COMPLETED = 2;

export const STATUS_LABELS = {
  [STATUS_CANCELLED]: 'Cancelled',
  [STATUS_PENDING]: 'Pending',
  [STATUS_COMPLETED]: 'Completed',
};

export const STATUS_OPTIONS = [
  { value: STATUS_CANCELLED, label: STATUS_LABELS[STATUS_CANCELLED] },
  { value: STATUS_PENDING, label: STATUS_LABELS[STATUS_PENDING] },
  { value: STATUS_COMPLETED, label: STATUS_LABELS[STATUS_COMPLETED] },
];

export const headCells = [
  {
    id: 'date',
    type: 'datetime',
    label: 'Date',
    width: 120,
  },
  {
    id: 'amount',
    type: 'number',
    label: 'Amount',
    width: 120,
    render: ({ row }) => {
      return <Typography color='primary' variant='h6'>{'HKD ' + thousands((row.amount || 0).toFixed(2))}</Typography>;
    },
  },
  {
    id: 'fund',
    label: 'Fund',
    width: 150,
    render: ({ row, locale }) => localizeFund(get(row, 'fund'), locale),
  },
  {
    id: 'type',
    label: 'Type',
    width: 130,
    render: ({ row, locale }) => {
      const _paymentMethod = get(row, 'onlinePayment.paymentMethod') || get(row, `paymentMethod.${locale === 'zh-Hant' ? 'nameChi' : locale === 'zh-Hans' ? 'nameChi' : 'nameEng'}`);
      return (<>
        <Typography variant='paragraph' color='secondary'><TranslatedText value={row.type} /></Typography>
        {
          row.subscription?.refId && (
            <div>
              <Typography variant='paragraph' color='info' sx={{ display: 'inline' }}>
                <TranslatedText value='Subscription Ref ID: ' />
              </Typography>
              <Link
                component="button"
                variant="body2"
                // onClick={(e) => {
                //   e.stopPropagation();
                // }}
                sx={{
                  fontFamily: 'monospace',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                {row.subscription?.refId}
                <FontAwesomeIcon icon={faArrowUpRightFromSquare} size="xs" />
              </Link>
            </div>
          )
        }
        {_paymentMethod && (
          <Typography variant="subtitle1" color="textSecondary">
            {_paymentMethod}
          </Typography>
        )}
      </>);
    },
  },
  {
    id: 'status',
    label: 'Status',
    width: 120,
    render: ({ row }) => <StatusChip status={row.status} />,
  },
  {
    id: 'refId',
    label: 'Ref ID',
    width: 150,
    render: ({ row }) => <code>{row.refId}</code>,
  },
];

export const validationRules = {
  date: {
    required: common.validationRules.required,
    validate: common.validationRules.date,
  },
  type: {
    required: common.validationRules.required,
    maxLength: common.validationRules.maxLength255,
  },
  amount: {
    required: common.validationRules.required,
    validate: (value) => {
      if (!value || isNaN(value)) return 'Amount must be a valid number';
      if (parseFloat(value) < 0.01) return 'Amount must be at least 0.01';
      if (parseFloat(value) > 100000000) return 'Amount must not exceed 100,000,000.00';
      if (!/^\d+(\.\d{1,2})?$/.test(value)) return 'Amount must have up to 2 decimal places';
      return true;
    },
  },
  giverName: {
    required: common.validationRules.required,
    maxLength: common.validationRules.maxLength255,
  },
  giverEmail: {
    required: common.validationRules.required,
    maxLength: common.validationRules.maxLength255,
    validate: common.validationRules.email,
  },
  fundId: {
    required: common.validationRules.required,
  },
  paymentMethodId: {
    required: common.validationRules.required,
  },
};

export const fields = {
  id: {
    name: 'id',
    type: 'hidden',
  },
  type: {
    name: 'type',
    type: 'select',
    label: 'Type',
    defaultValue: 'Once',
  },
  date: {
    name: 'date',
    label: 'Date',
    type: 'datetime',
    defaultValue: moment().format('YYYY-MM-DDTHH:mm'),
  },
  amount: {
    name: 'amount',
    label: 'Amount',
    type: 'currency',
    // startAdornment: <InputAdornment position="start">HKD</InputAdornment>,
    // placeholder: 'HKD 0.00',
  },
  giverName: {
    name: 'giverName',
    label: 'Full Name',
    type: 'text',
    // helperText: 'Name to be used on confirmation email',
  },
  giverEmail: {
    name: 'giverEmail',
    label: 'Email',
    type: 'email',
  },
  fundId: {
    name: 'fundId',
    type: 'select',
    label: 'Fund',
  },
  paymentMethodId: {
    name: 'paymentMethodId',
    type: 'select',
    label: 'Payment Method',
  },
};

export const form = {
  create: [
    ['fundId'],
    ['type'],
    ['paymentMethodId'],
    ['amount'],
  ],
  edit: [
    ['fundId'],
    ['type'],
    ['date'],
    ['amount'],
    ['paymentMethodId'],
  ],
  public: [
    ['fundId'],
    ['type'],
    ['paymentMethodId'],
    ['amount'],
    ['giverName'],
    ['giverEmail'],
  ],
  view: [
    ['fundId'],
    ['type'],
    ['date'],
    ['amount'],
    ['paymentMethodId'],
  ],
};

export const optionFilters = [];

export default {
  validationRules,
  headCells,
  fields,
  form,
  optionFilters,
};
